// ./src/Components/Chat/ChatComponente/ChatNovoChatSender.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./ChatNovoChatSender.css";

export default function ChatNovoChatSender({ aberto, onFechar, onIniciar }) {
    const [ddd, setDdd] = useState("");
    const [numero, setNumero] = useState("");
    const [erro, setErro] = useState("");

    const modalRef = useRef(null);
    const inputDddRef = useRef(null);

    const titleId = useMemo(
        () => `chat-novo-title-${Math.random().toString(36).slice(2)}`,
        []
    );

    const somenteDigitos = (v) => (v || "").toString().replace(/\D/g, "");

    const limparCampos = () => {
        setDdd("");
        setNumero("");
        setErro("");
    };

    const limparErroSePreciso = () => {
        if (erro) setErro("");
    };

    const fechar = () => {
        if (onFechar) onFechar();
    };

    const validar = () => {
        const d = somenteDigitos(ddd);
        const n = somenteDigitos(numero);

        if (d.length !== 2) return "DDD deve ter 2 dígitos.";

        // Número SEM DDD: 8 (fixo) ou 9 (celular)
        if (!(n.length === 8 || n.length === 9)) return "Número inválido (8 ou 9 dígitos).";

        return "";
    };

    const iniciar = () => {
        const e = validar();
        if (e) {
            setErro(e);
            return;
        }

        const d = somenteDigitos(ddd);
        const n = somenteDigitos(numero);

        if (onIniciar) onIniciar({ ddi: "55", ddd: d, telefone: n });
        fechar();
    };

    // ===== Handlers (sem inline functions) =====

    const handleOverlayMouseDown = (e) => {
        if (e.target !== e.currentTarget) return;
        fechar();
    };

    const handleModalMouseDown = (e) => {
        e.stopPropagation();
    };

    const handleCloseClick = () => {
        fechar();
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        iniciar();
    };

    const handleDddChange = (e) => {
        limparErroSePreciso();
        const value = somenteDigitos(e.target.value).slice(0, 2);
        setDdd(value);
    };

    const handleNumeroChange = (e) => {
        limparErroSePreciso();
        const value = somenteDigitos(e.target.value).slice(0, 9);
        setNumero(value);
    };

    const trapFocusTab = (e) => {
        const root = modalRef.current;
        if (!root) return;

        const focusables = root.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );

        if (!focusables.length) return;

        const first = focusables[0];
        const last = focusables[focusables.length - 1];

        if (e.shiftKey) {
            if (document.activeElement === first) {
                e.preventDefault();
                last.focus();
            }
            return;
        }

        if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
        }
    };

    const handleWindowKeyDown = (e) => {
        if (e.key === "Escape") {
            fechar();
            return;
        }

        if (e.key === "Tab") {
            trapFocusTab(e);
        }
    };

    useEffect(() => {
        if (!aberto) return;

        limparCampos();

        // foco no DDD
        requestAnimationFrame(() => {
            if (inputDddRef.current) inputDddRef.current.focus();
        });

        // trava scroll do body
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        window.addEventListener("keydown", handleWindowKeyDown);

        return () => {
            document.body.style.overflow = prevOverflow;
            window.removeEventListener("keydown", handleWindowKeyDown);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aberto]);

    if (!aberto) return null;

    return (
        <div
            className="chat-novo-modal-overlay"
            role="presentation"
            onMouseDown={handleOverlayMouseDown}
        >
            <div
                ref={modalRef}
                className="chat-novo-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                onMouseDown={handleModalMouseDown}
            >
                {/* HEADER */}
                <div className="chat-novo-modal-header">
                    <div id={titleId} className="chat-novo-title">
                        Nova conversa
                    </div>

                    <button
                        type="button"
                        className="chat-novo-close"
                        onClick={handleCloseClick}
                        aria-label="Fechar"
                        title="Fechar"
                    >
                        ✕
                    </button>
                </div>

                {/* BODY + ENTER */}
                <form className="chat-novo-modal-body" onSubmit={handleSubmit}>
                    {erro ? <div className="chat-novo-erro">{erro}</div> : null}

                    <div className="chat-novo-grid">
                        <div className="chat-novo-field">
                            <label className="chat-novo-label">DDD</label>
                            <input
                                ref={inputDddRef}
                                type="text"
                                className="chat-novo-input"
                                value={ddd}
                                onChange={handleDddChange}
                                placeholder="11"
                                autoComplete="off"
                                inputMode="numeric"
                                maxLength={2}
                            />
                        </div>

                        <div className="chat-novo-field chat-novo-field--wide">
                            <label className="chat-novo-label">Número</label>
                            <input
                                type="text"
                                className="chat-novo-input"
                                value={numero}
                                onChange={handleNumeroChange}
                                placeholder="912345678"
                                autoComplete="off"
                                inputMode="numeric"
                                maxLength={9}
                            />
                        </div>
                    </div>

                    <div className="chat-novo-hint">Informe apenas dígitos (sem hífen/espaços).</div>

                    {/* FOOTER */}
                    <div className="chat-novo-modal-footer">
                        <button type="submit" className="chat-novo-iniciar">
                            <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                className="chat-novo-icon"
                                aria-hidden="true"
                            >
                                <path
                                    d="M4 4H20V15C20 15.5523 19.5523 16 19 16H7L4 19V4Z"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    strokeLinejoin="round"
                                />
                                <path
                                    d="M12 8V13"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    strokeLinecap="round"
                                />
                                <path
                                    d="M9.5 10.5H14.5"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    strokeLinecap="round"
                                />
                            </svg>
                            Iniciar conversa
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
